// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { CurrencyUtils } from "../../../../../../../io/hyperify/core/CurrencyUtils";
import { MY_PAYMENT_VIEW_CLASS_NAME } from "../../../../../core/constants/className";
import { TranslationFunction } from "../../../../../../../io/hyperify/core/types/TranslationFunction";
import { useParams } from "react-router-dom";
import { useMyPayment } from "../../../../hooks/useMyPayment";
import { Loader } from "../../../../../../../io/hyperify/frontend/components/loader/Loader";
import {
    T_DATE_FORMAT,
    T_MY_PAYMENT_VIEW_AMOUNT_LABEL,
    T_MY_PAYMENT_VIEW_DESCRIPTION_LABEL,
    T_MY_PAYMENT_VIEW_DISCOUNT_PERCENT_LABEL,
    T_MY_PAYMENT_VIEW_DUE_DATE_LABEL,
    T_MY_PAYMENT_VIEW_END_DATE_LABEL,
    T_MY_PAYMENT_VIEW_EXTRA_NOTICE_LABEL,
    T_MY_PAYMENT_VIEW_INVOICE_ID_LABEL,
    T_MY_PAYMENT_VIEW_NO_RESULTS_FOUND,
    T_MY_PAYMENT_VIEW_PAYMENT_ID_LABEL,
    T_MY_PAYMENT_VIEW_PRICE_LABEL,
    T_MY_PAYMENT_VIEW_START_DATE_LABEL,
    T_MY_PAYMENT_VIEW_TITLE,
    T_MY_PAYMENT_VIEW_VAT_PERCENT_LABEL,
} from "../../../../../core/constants/translation";
import { TranslationParams } from "../../../../../../../io/hyperify/core/types/TranslationParams";
import { CardGrid } from "../../../../../../../io/hyperify/frontend/components/cardGrid/CardGrid";
import { Card } from "../../../../../../../io/hyperify/frontend/components/card/Card";
import { DateCard } from "../../../../../../../io/hyperify/frontend/components/card/date/DateCard";
import "./MyPaymentView.scss";

export interface MyPaymentViewProps {
    readonly t : TranslationFunction;
    readonly className ?: string;
}

export function MyPaymentView (props: MyPaymentViewProps) {
    const t = props?.t;
    const className = props?.className;
    const params = useParams();
    const clientId = params?.clientId;
    const paymentId = params?.paymentId;
    const [payment] = useMyPayment(paymentId, clientId);
    const notFound = payment === null;
    const isLoading = payment === undefined;
    const translationParams : TranslationParams = {
        PAYMENT_ID: paymentId ?? ''
    };
    const dateFormat = t(T_DATE_FORMAT);
    return (
        <div className={
            MY_PAYMENT_VIEW_CLASS_NAME
            + (className? ` ${className}` : '')
        }>
            <h3>{t(T_MY_PAYMENT_VIEW_TITLE, translationParams)}</h3>
            <div className={`${MY_PAYMENT_VIEW_CLASS_NAME}-details`}>{
                isLoading? <Loader /> : (
                    <>
                        {notFound ? (
                            <p>{t(T_MY_PAYMENT_VIEW_NO_RESULTS_FOUND, translationParams)}</p>
                        ) : (
                            <>
                                <CardGrid>
                                    <Card label={t(T_MY_PAYMENT_VIEW_PAYMENT_ID_LABEL)}>{payment?.paymentId}</Card>
                                    { payment?.invoiceId && payment?.invoiceId !== '0' ? <Card label={t(T_MY_PAYMENT_VIEW_INVOICE_ID_LABEL)}>{payment?.invoiceId}</Card> : null}
                                    <DateCard label={t(T_MY_PAYMENT_VIEW_START_DATE_LABEL)} value={payment?.startDate} format={dateFormat} />
                                    <DateCard label={t(T_MY_PAYMENT_VIEW_END_DATE_LABEL)} value={payment?.endDate} format={dateFormat} />
                                    <DateCard label={t(T_MY_PAYMENT_VIEW_DUE_DATE_LABEL)} value={payment?.dueDate} format={dateFormat} />
                                </CardGrid>

                                <CardGrid>
                                    <Card label={t(T_MY_PAYMENT_VIEW_AMOUNT_LABEL)}>{payment?.amount}</Card>
                                    <Card label={t(T_MY_PAYMENT_VIEW_PRICE_LABEL)}>{`${CurrencyUtils.getSumWithVat(payment?.price, 1, payment?.vatPercent, payment?.discountPercent)?.toFixed(2)}`}</Card>
                                    <Card label={t(T_MY_PAYMENT_VIEW_VAT_PERCENT_LABEL)}>{`${(payment?.vatPercent*100).toFixed(0)}%`}</Card>
                                    { payment?.discountPercent ? <Card label={t(T_MY_PAYMENT_VIEW_DISCOUNT_PERCENT_LABEL)}>{`${(payment?.discountPercent*100).toFixed(0)}%`}</Card> : null}
                                </CardGrid>

                                <Card label={t(T_MY_PAYMENT_VIEW_DESCRIPTION_LABEL)}>{payment?.description}</Card>

                                {payment?.extraNotice ? <Card label={t(T_MY_PAYMENT_VIEW_EXTRA_NOTICE_LABEL)}>{payment?.extraNotice}</Card> : null}

                            </>
                        )}
                    </>
                )
            }</div>
        </div>
    );
}
